import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { crud, kullaniciYetkiAta, yetkiSablonSeed } from "../../lib/api";

const MODUL_LABELS = {
    "sigorta-dosyalari": "Dosya",
    odemeler: "Ödeme",
    evraklar: "Evrak",
    "kasa-hesaplar": "Muhasebe",
    "odeme-raporlari": "Rapor",
    "ai-sohbet": "AI",
    "dosya-yorumlari": "Yorum",
};

function permIcon(mod) {
    const p = mod || {};
    if (p.delete) return "✓ Sil";
    if (p.write) return "✓ Düzenle";
    if (p.read) return "👁 Gör";
    return "✗";
}

export default function YetkiSablonlariPage() {
    const [sablonlar, setSablonlar] = useState([]);
    const [kullanicilar, setKullanicilar] = useState([]);
    const [profiller, setProfiller] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedSablon, setSelectedSablon] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, k, p] = await Promise.all([
                crud.list("yetki-sablonlari"),
                crud.list("kullanicilar"),
                crud.list("kullanici-yetki-profilleri"),
            ]);
            setSablonlar(Array.isArray(s) ? s : s.results || []);
            setKullanicilar(Array.isArray(k) ? k : k.results || []);
            setProfiller(Array.isArray(p) ? p : p.results || []);
        } catch {
            toast.error("Veriler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        yetkiSablonSeed().catch(() => {});
    }, [load]);

    async function ata(e) {
        e.preventDefault();
        if (!selectedUser) return;
        try {
            await kullaniciYetkiAta({
                user_id: selectedUser,
                sablon_id: selectedSablon || null,
            });
            toast.success("Yetki şablonu atandı.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Atama başarısız.");
        }
    }

    return (
        <div className="p-6 max-w-5xl">
            <div className="mb-6">
                <h1 className="font-display text-2xl text-white flex items-center gap-2">
                    <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
                    Kullanıcılar & Yetkiler
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Rol şablonları — sekreter, avukat, muhasebe, stajyer
                </p>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-slate-400 py-8">
                    <Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {sablonlar.map((s) => (
                            <div
                                key={s.id}
                                className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4"
                            >
                                <div className="font-semibold text-white mb-2">{s.ad}</div>
                                <ul className="text-[11px] text-slate-400 space-y-1">
                                    {Object.entries(s.izinler || {})
                                        .filter(([k]) => MODUL_LABELS[k])
                                        .slice(0, 6)
                                        .map(([k, v]) => (
                                            <li key={k}>
                                                {MODUL_LABELS[k]}: {permIcon(v)}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <section className="rounded-xl border border-[#D4AF37]/25 bg-[#121A2F]/80 p-4 mb-6">
                        <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                            <UserCheck className="h-4 w-4 text-[#D4AF37]" />
                            Kullanıcıya Şablon Ata
                        </h3>
                        <form onSubmit={ata} className="flex flex-wrap gap-3 items-end">
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    Kullanıcı
                                </label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm min-w-[180px]"
                                >
                                    <option value="">Seçin</option>
                                    {kullanicilar.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.username}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    Şablon
                                </label>
                                <select
                                    value={selectedSablon}
                                    onChange={(e) => setSelectedSablon(e.target.value)}
                                    className="bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm min-w-[180px]"
                                >
                                    <option value="">— Yok (tam yetki profil yoksa) —</option>
                                    {sablonlar.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.ad}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="h-10 px-4 rounded-md bg-[#D4AF37] text-slate-900 text-sm font-semibold"
                            >
                                Kaydet
                            </button>
                        </form>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                        <h3 className="text-sm font-semibold text-slate-300 mb-3">
                            Mevcut Atamalar
                        </h3>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-500 text-xs">
                                    <th className="pb-2">Kullanıcı</th>
                                    <th className="pb-2">Şablon</th>
                                </tr>
                            </thead>
                            <tbody>
                                {profiller.map((p) => (
                                    <tr key={p.id} className="border-t border-white/5">
                                        <td className="py-2 text-slate-300">
                                            {p.user_label || p.user}
                                        </td>
                                        <td className="py-2 text-slate-400">
                                            {p.sablon_label || p.sablon || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}
        </div>
    );
}
